import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Search, AlertTriangle, Link2, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import api from "../lib/api";

// Status pill colour map — visual cue in the mandate list.
const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending_submission: "bg-amber-100 text-amber-800 border-amber-200",
  pending_customer_approval: "bg-amber-100 text-amber-800 border-amber-200",
  submitted: "bg-sky-100 text-sky-800 border-sky-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-stone-200 text-stone-700 border-stone-300",
  expired: "bg-stone-200 text-stone-700 border-stone-300",
};

function formatCreated(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

/**
 * RelinkGoCardlessModal — search GoCardless by email and let an admin
 * pick a customer + mandate to relink the franchisee to. Never writes
 * silently: the admin must radio-select a mandate + click Confirm.
 *
 * Props:
 *   franchisee   — full franchisee doc (needs id + email/mojo_email/secondary_email)
 *   onClose()    — close without saving
 *   onRelinked(fresh)  — called with the updated franchisee doc after a
 *                        successful relink; parent should re-render.
 */
export default function RelinkGoCardlessModal({ franchisee, onClose, onRelinked }) {
  const defaultEmail = franchisee.email || franchisee.mojo_email || franchisee.secondary_email || "";
  const [query, setQuery] = useState(defaultEmail);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState(null); // null=untouched, []=none found, [...]=matches
  const [pick, setPick] = useState(null); // { customer_id, mandate_id }
  const [confirming, setConfirming] = useState(false);

  const runSearch = useCallback(async (emailToSearch) => {
    if (!emailToSearch || !emailToSearch.includes("@")) {
      setError("Please enter an email address to search.");
      return;
    }
    setSearching(true); setError(""); setPick(null);
    try {
      const { data } = await api.get(`/gocardless/customers/search`, { params: { email: emailToSearch } });
      setCustomers(data.customers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Search failed. Check GoCardless config.");
      setCustomers([]);
    } finally { setSearching(false); }
  }, []);

  // Auto-run search on mount when we have an email.
  useEffect(() => {
    if (defaultEmail) runSearch(defaultEmail);
  }, [defaultEmail, runSearch]);

  const totalMandates = useMemo(
    () => (customers || []).reduce((acc, c) => acc + (c.mandates?.length || 0), 0),
    [customers],
  );

  const confirmRelink = async () => {
    if (!pick) return;
    setConfirming(true); setError("");
    try {
      const { data } = await api.post(`/gocardless/franchisees/${franchisee.id}/relink`, pick);
      if (data.ok && data.franchisee) {
        onRelinked(data.franchisee);
      } else {
        setError("Relink returned no data. Please refresh and try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Relink failed. Check GoCardless config and try again.");
    } finally { setConfirming(false); }
  };

  const currentlyLinked = franchisee.gocardless_mandate_id;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-stone-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      data-testid="relink-gc-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white border border-stone-200 rounded-2xl shadow-2xl my-10">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">GoCardless</div>
            <h2 className="text-xl font-display font-black text-stone-950 mt-1">Relink Customer &amp; Mandate</h2>
          </div>
          <button onClick={onClose} data-testid="relink-close" className="w-9 h-9 flex items-center justify-center hover:bg-stone-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {currentlyLinked && (
            <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
              <span className="font-semibold text-stone-800">Currently linked:</span>{" "}
              customer <code className="font-mono text-[11px]">{franchisee.gocardless_customer_id || "—"}</code>{" · "}
              mandate <code className="font-mono text-[11px]">{currentlyLinked}</code>{" "}
              <span className="text-stone-500">({franchisee.gocardless_mandate_status || "unknown"})</span>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2 block">
              Search GoCardless by email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(query); }}
                placeholder="email@example.com"
                data-testid="relink-search-input"
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-stone-950"
              />
              <button
                onClick={() => runSearch(query)}
                disabled={searching}
                data-testid="relink-search-btn"
                className="px-4 py-2 bg-stone-950 hover:bg-stone-800 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider rounded-lg inline-flex items-center gap-1.5"
              >
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {searching ? "Searching…" : "Search"}
              </button>
            </div>
            <div className="text-[11px] text-stone-500 mt-1.5">
              Exact match, case-insensitive. Franchisee&apos;s primary email is prefilled — change it if the new bank was set up under a different address.
            </div>
          </div>

          {error && (
            <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="relink-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {/* Results */}
          {customers !== null && !searching && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">
                {customers.length === 0
                  ? "No GoCardless customers with that email"
                  : `${customers.length} customer${customers.length === 1 ? "" : "s"} · ${totalMandates} mandate${totalMandates === 1 ? "" : "s"}`}
              </div>
              {customers.length === 0 ? (
                <div className="text-sm text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-4 py-4">
                  If the new customer was set up under a different email, edit the search box and re-run. Otherwise ask the franchisee to confirm which email their bank details were entered against in GoCardless.
                </div>
              ) : (
                <div className="space-y-3">
                  {customers.map((cust) => (
                    <div key={cust.customer_id} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`relink-customer-${cust.customer_id}`}>
                      <div className="bg-stone-50 px-4 py-2.5 border-b border-stone-200 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-stone-900">
                            {[cust.given_name, cust.family_name].filter(Boolean).join(" ") || cust.email || cust.customer_id}
                          </div>
                          <div className="text-[11px] text-stone-500 tabular-nums">
                            {cust.email} · created {formatCreated(cust.created_at)}
                          </div>
                        </div>
                        <a
                          href={`https://manage.gocardless.com/customers/${cust.customer_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[10px] uppercase tracking-widest font-bold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                          data-testid={`relink-open-customer-${cust.customer_id}`}
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      {cust.mandates.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-stone-500">No mandates for this customer.</div>
                      ) : (
                        <div className="divide-y divide-stone-100">
                          {cust.mandates.map((m) => {
                            const selected = pick && pick.customer_id === cust.customer_id && pick.mandate_id === m.mandate_id;
                            const alreadyLinked = m.mandate_id === franchisee.gocardless_mandate_id;
                            const badge = STATUS_STYLES[m.status] || "bg-stone-100 text-stone-700 border-stone-200";
                            return (
                              <label
                                key={m.mandate_id}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                                  selected ? "bg-emerald-50" : "hover:bg-stone-50"
                                }`}
                                data-testid={`relink-mandate-${m.mandate_id}`}
                              >
                                <input
                                  type="radio"
                                  name="mandate-pick"
                                  checked={!!selected}
                                  onChange={() => setPick({ customer_id: cust.customer_id, mandate_id: m.mandate_id })}
                                  disabled={alreadyLinked}
                                  className="w-4 h-4"
                                  data-testid={`relink-mandate-radio-${m.mandate_id}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${badge}`}>
                                      {m.status.replace(/_/g, " ")}
                                    </span>
                                    <code className="font-mono text-xs text-stone-800">{m.reference || m.mandate_id}</code>
                                    <span className="text-[11px] text-stone-500">{m.scheme?.toUpperCase()}</span>
                                    {alreadyLinked && (
                                      <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">· currently linked</span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-stone-500 tabular-nums mt-0.5">
                                    {m.bank_name || "Bank"} {m.account_ending ? `•••• ${m.account_ending}` : ""} · created {formatCreated(m.created_at)}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-stone-200 bg-stone-50 flex items-center justify-between rounded-b-2xl">
          <div className="text-[11px] text-stone-500">
            {pick ? (
              <span className="text-stone-800">Selected mandate <code className="font-mono">{pick.mandate_id}</code></span>
            ) : (
              "Select a mandate above to enable Confirm."
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-100 rounded-lg"
              data-testid="relink-cancel"
            >
              Cancel
            </button>
            <button
              onClick={confirmRelink}
              disabled={!pick || confirming}
              data-testid="relink-confirm"
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider rounded-lg inline-flex items-center gap-1.5"
            >
              {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {confirming ? "Relinking…" : "Confirm relink"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * PossibleRelinkStrip — inline banner shown inside the GoCardlessPanel
 * when the refresh response returned a ``possible_relink`` hint. Two
 * actions: "Link this mandate" (one-click) or "Dismiss" (hide until
 * next refresh).
 */
export function PossibleRelinkStrip({ hint, franchisee, onRelinked }) {
  const [linking, setLinking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState("");
  if (!hint || dismissed) return null;

  const linkNow = async () => {
    setLinking(true); setError("");
    try {
      const { data } = await api.post(`/gocardless/franchisees/${franchisee.id}/relink`, {
        customer_id: hint.customer_id, mandate_id: hint.mandate_id,
      });
      if (data.ok && data.franchisee) onRelinked(data.franchisee);
    } catch (e) {
      setError(e?.response?.data?.detail || "Link failed. Try the manual Relink Customer flow.");
    } finally { setLinking(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3" data-testid="possible-relink-strip">
      <div className="flex items-start gap-3">
        <Link2 className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-800">Possible active mandate found</div>
          <div className="text-sm text-amber-900 mt-1 flex items-baseline gap-2 flex-wrap">
            <code className="font-mono text-xs">{hint.reference || hint.mandate_id}</code>
            {(hint.bank_name || hint.account_ending) && (
              <span className="text-amber-800">
                · {hint.bank_name || "Bank"}{hint.account_ending ? ` •••• ${hint.account_ending}` : ""}
              </span>
            )}
            <span className="text-amber-700 text-xs">· under GC customer <code className="font-mono">{hint.customer_id}</code></span>
          </div>
          {error && <div className="text-xs text-red-800 mt-2">{error}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={linkNow}
            disabled={linking}
            data-testid="possible-relink-link-btn"
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-wider rounded inline-flex items-center gap-1"
          >
            {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
            Link this mandate
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 text-amber-700 hover:bg-amber-100 rounded"
            title="Hide until next refresh"
            data-testid="possible-relink-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
